import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuthBootstrap from "../../../auth/useAuthBootstrap";
import { logout } from "../../../services/api";
import { Button, Dialog } from "../../../design-v2";
import { suppressLocalRecovery } from "../../workoutLog/recovery/recoverySuppression";
import { workoutLogStore } from "../../workoutLog/store/localExecutionStore";
import { workoutSyncEngine } from "../../workoutLog/sync/syncEngine";
import SettingsSection from "../SettingsSection";

const LOGOUT_ERROR = "We couldn't sign you out. Please try again.";

export default function AccountActionsSection() {
  const bootstrap = useAuthBootstrap();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [logoutError, setLogoutError] = useState("");

  async function performLogout(userId) {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    setLogoutError("");

    try {
      await logout();
      // Only now that the server revoked the Session, and before the
      // best-effort cleanup below that may fail: this user is never
      // automatically recovered here again until they sign in again.
      if (userId && !suppressLocalRecovery(userId)) {
        console.warn("Workout Log could not persist the logout recovery suppression.");
      }
      try {
        if (userId && typeof indexedDB !== "undefined") {
          try {
            await workoutLogStore.purgeAfterLogout(userId);
          } finally {
            // Only now that the server revoked the Session: an explicit logout
            // never leaves this user as the device's local recovery owner. A
            // failed or offline logout never reaches this line (§11.6).
            await workoutLogStore.clearLastAuthenticatedUserId(userId);
          }
        }
      } finally {
        // The Session is already revoked at this point. A local cleanup failure
        // must not leave the UI pretending that authentication still exists.
        bootstrap.completeLogout();
        navigate("/login", { replace: true });
      }
    } catch {
      setLogoutError(LOGOUT_ERROR);
    } finally {
      setIsLoggingOut(false);
    }
  }

  async function handleLogout() {
    if (isLoggingOut || isSyncing) return;
    const userId = bootstrap.user?.id;
    try {
      const hasPending = userId && typeof indexedDB !== "undefined"
        ? await workoutLogStore.hasUnacknowledged(userId)
        : false;
      if (hasPending) {
        setShowPendingDialog(true);
        return;
      }
      await performLogout(userId);
    } catch {
      setLogoutError(LOGOUT_ERROR);
    }
  }

  async function handleSyncNow() {
    const userId = bootstrap.user?.id;
    if (!userId || isSyncing) return;
    setIsSyncing(true);
    setLogoutError("");
    try {
      await workoutSyncEngine.syncNow(userId);
      if (await workoutLogStore.hasUnacknowledged(userId)) return;
      setShowPendingDialog(false);
      await performLogout(userId);
    } catch {
      // The Dialog remains open with the canonical recovery actions. The local
      // records stay untouched and the sync status carries the concrete error.
    } finally {
      setIsSyncing(false);
    }
  }

  return (
    /*
     * Restrained danger semantics: one clearly named destructive action,
     * separated from Profile and Connected Accounts by its own group label
     * rather than by an extra nested surface.
     */
    <SettingsSection title="Account">
      <Button
        type="button"
        variant="danger"
        className="lz-settings-block-action"
        disabled={isLoggingOut || isSyncing}
        isLoading={isLoggingOut}
        loadingLabel="Logging out..."
        onClick={handleLogout}
      >
        Log out
      </Button>
      {logoutError ? (
        <p className="lz-settings-error" role="alert">
          {logoutError}
        </p>
      ) : null}
      <Dialog
        open={showPendingDialog}
        onClose={() => setShowPendingDialog(false)}
        title="Workout data hasn't synced yet."
        description="Connect to sync before signing out."
        showCloseButton={false}
        footer={(
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowPendingDialog(false)}
              disabled={isSyncing}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSyncNow}
              isLoading={isSyncing}
              loadingLabel="Syncing..."
            >
              Sync now
            </Button>
          </>
        )}
      />
    </SettingsSection>
  );
}
